import React from 'react';
import PropTypes from 'prop-types';
import {
  getAndClearDisplayOptions,
  getDisplayOptions,
} from 'AppSrc/core/components/forms/form-base-components/redux/display-options/actions-display-options';
import { FormSection } from 'AppSrc/core/components/forms/form-section';
import { prefixStringWithIndexPlusOne } from 'AppSrc/core/utility-functions/string-fun';
import { navigationPropsShape } from 'AppSrc/core/components/forms/section-nav';
import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { userRolesPropShape } from 'AppSrc/core/auth/auth-proptypes';
import { getDisplayOptionsToUse } from 'AppSrc/core/components/forms/data/get-display-options-to-use';
import { getProdCatsForServices } from 'AppSrc/core/components/forms/data/get-prod-cats-for-services';
import {
  PROJECT_MOBILE,
  NON_PROJECT_MOBILE,
  RECORDABLE,
  NOTIFIABLE,
  MOBILE,
  FIXED,
  IMPACT_LEVEL_1,
  IMPACT_LEVEL_2,
  RISK_LEVEL_3,
  RISK_LEVEL_4,
} from 'AppSrc/core/components/forms/value-dependency-constants';
// Key values that are repeated a lot
import { defaultFieldLogic } from 'AppSrc/core/components/forms/form-base-components/data-structure/default-field-logic';
import {
  formDataShape,
} from 'AppSrc/core/components/forms/form-base-components/data-structure/v-f-proptypes';
import {
  getRiskValue,
  getImpactLevel,
  userRoleForSelectedNetwork,
  currentSelectedNetwork,
  getIsOlaCrq,
  getIsInternationalCrq,
} from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import {
  CHANGE_DETAILS,
  SECTION_TITLES,
} from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';
// form fields
import { whichServiceNeedsToChange } from './which-service-needs-to-change';

import { summaryOfChange } from './summary-of-change';

import { changeDetailsNotes } from './change-details-notes';
import { customerImpactStatement } from './customer-impact-statement';
import { changeReason } from './change-reason';
import { productCategorisationOne } from './product-categorisation-one';
import { productCategorisationTwo } from './product-categorisation-two';
import { productCategorisationThree } from './product-categorisation-three';
import { operationalCategorisationOne } from './operational-categorisation-one';
import { operationalCategorisationTwo } from './operational-categorisation-two';
import { operationalCategorisationThree } from './operational-categorisation-three';
/* MOBILE only fields */
import { tmbs } from './tmbs';
import { babt } from './babt';
import { iarNumber } from './iar-number';
import { mvno } from './mvno';
import { assignSponsor } from './assign-sponsor';
import { commQuestion } from './comms-question';
import { paragraphCommRequired } from './paragrapsh-comm-required';
/* FIXED only fields */
import { company } from './company';
import { region } from './region';
import { siteGroup } from './site-group';
import { site } from './site';
import { productName } from './product-name';
import { productModel } from './product-model';
import { manufacturer } from './manufacturer';
/* Sub Section Titles */
import { sstChangeLocation } from './sst-change-location';

// Section ID (for the data layer)
const sectionId = CHANGE_DETAILS;
// Title for the main form
const sectionTitle = SECTION_TITLES[CHANGE_DETAILS];


// This maps on to the data structure created by the generate-display-data.js service
const getGeneratedKey = (params) => {
  const {
    thisField,
    dependantFields,
    values,
    selectedNetwork,
  } = params;

  const lacksDependantValue = dependantFields.some((dependantField) => values[dependantField.id] === undefined || values[dependantField.id] === null);

  const baseKey = `${thisField.id}${selectedNetwork ? `${`-${selectedNetwork}`}` : ''}`;

  if (lacksDependantValue) {
    // default to fieldId if we cannot generate a dynamic key
    return baseKey;
  }

  const dependantValues = dependantFields.map((dependantField) => values[dependantField.id]);

  return `${baseKey}-${dependantValues.join('-')}`;
};


const prepopulateOpcat = (selectedNetwork) => (callBackParams) => {
  const {
    form,
    thisField,
    newValue,
  } = callBackParams;
  const {
    name,
  } = thisField;

  const {
    setValues,
    values,
    setFieldTouched,
  } = form;

  // gets the old values for all of the form and mixes in the new value
  const newValues = {
    ...values,
    [operationalCategorisationOne.id]: newValue,
    [name]: newValue,
  };

  // set the new form data with/without side affects
  setValues(newValues);


  setFieldTouched({
    [name]: true,
  });
};


const prepopulateTiersOnUpdateValue = (selectedNetwork) => (callBackParams) => {
  const {
    form,
    thisField,
    newValue,
  } = callBackParams;

  const {
    name,
  } = thisField;

  const {
    setValues,
    values,
    setFieldTouched,
  } = form;

  const prodCats = getProdCatsForServices({
    value: newValue,
    selectedNetwork,
  });

  // gets the old values for all of the form and mixes in the new value
  const newValues = {
    ...values,
    [productCategorisationOne.id]: prodCats[0],
    [productCategorisationTwo.id]: prodCats[1],
    [productCategorisationThree.id]: prodCats[2],
    [name]: newValue,
  };

  // set the new form data with/without side affects
  setValues(newValues);


  setFieldTouched({
    [name]: true,
  });
};



const setManufacturerValueBasedOnModel = (selectedNetwork) => (callBackParams) => {
  const {
    form,
    thisField,
    newValue,
  } = callBackParams;

  const {
    name,
  } = thisField;

  const {
    setValues,
    values,
    setFieldTouched,
  } = form;

  // gets the old values for all of the form and mixes in the new value
  const newValues = {
    ...values,
    [name]: newValue,
  };

  const manufacturerKey = getGeneratedKey({
    thisField: manufacturer,
    dependantFields: [
      productName,
      productModel,
    ],
    values: newValues,
  });
  const manufacturerDisplayOptions = getDisplayOptionsToUse({
    generatedKey: manufacturerKey,
  });

  const manufacturerValue = manufacturerDisplayOptions.length > 0 ? manufacturerDisplayOptions[0].value : '';


  const newValuesWithManufacturerValue = {
    ...newValues,
    [manufacturer.id]: manufacturerValue,
  };

  // set the new form data with/without side affects
  setValues(newValuesWithManufacturerValue);

  // set the field as touched
  setFieldTouched({
    [name]: true,
  });
};





// This creates a callback to set the value of `thisFieldId` normally whilst also
// setting `affectedFields` to their initial value. This is used in the tier fields,
// where tier 2 is disabled until tier 1 is selected. The same applies to tier 3 until
// tier 2 is selected. Whilst this logic *isn't* the logic to handle that, this is the
// logic to reset tier 2/3 values to null if tier 1/2 change their values.
const updateThisValueThenSetAffectedFieldsToInitialValue = (params) => {

  const {
    thisFieldId,
    affectedFields,
  } = params;

  return (callBackParams) => {
    const {
      form,
      newValue,
    } = callBackParams;

    const {
      setValues,
      values,
      setFieldTouched,
    } = form;

    const existingKeys = Object.keys(values);
    const oldValue = values[thisFieldId];

    // gets the old values for all of the form and mixes in the new value
    const newValues = {
      ...values,
      [thisFieldId]: newValue,
    };

    // defines the side affects we want this field to have
    const sideAffects = {};

    affectedFields.forEach((field) => {
      // sometimes we pass in components that are not relivant. ie, components that exist on fixed
      // but not mobile
      if (existingKeys.includes(field.id)) {
        sideAffects[field.id] = field.initialValues;
        // we're setting values here so it's a little aggresive to tell the user to fix them
        setFieldTouched(field.id, false);
        return null;
      }
      return null;
    });

    // defines a base of what the new state of the form will be
    let changeValues = {
      ...newValues,
    };

    // if the value has changed, add the side affects
    if (oldValue !== newValue) {
      changeValues = {
        ...newValues,
        ...sideAffects,
      };
    }

    // set the new form data with/without side affects
    setValues(changeValues);

    setFieldTouched(thisFieldId, true);
  };
};


// FN: 05/03/2020
// This is not the nicest code ever >.< Basically this generates a call back based on params from the section level.
// This callback is curried using information from the field level (newValue) which then return a function that gets
// dispatched by the drop down. The dispatched method gets the displayOptions for the field
const getSiteDisplayOptionsForNextTier = (params) => (newValue) => {

  const {
    endpointParams = {},
    thisFieldId,
    fieldIdDisplayOptionsToGet,
    displayOptionsToClear,
  } = params;

  return getAndClearDisplayOptions({
    fieldId: fieldIdDisplayOptionsToGet,
    endpointKey: 'change_location',
    endpointParams: {
      ...endpointParams,
      [thisFieldId]: newValue,
    },
    displayOptionsToClear,
  });
};


// callback for the form section to figure out what fields to render with conditional logic
const getCurrentFields = (params) => {

  const {
    values = {},
    // touched = {},
    userRoles,
    formData,
  } = params;

  const roleForNetwork = userRoleForSelectedNetwork(formData, userRoles);
  const selectedNetwork = currentSelectedNetwork(formData);
  // const isOloCrq = getIsOlaCrq(formData);
  const changeDetails = 'CHANGE_DETAILS';
  const isOloByService = formData.dataByFormSectionId[changeDetails] ? formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: Other Licensed Operator (OLO)' || formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: International Other Licensed Operator (OLO)' : null;
  const isOloCrq = getIsOlaCrq(formData) || isOloByService;
  const isIcmCrq = getIsInternationalCrq(formData);
  const isOLOInternational = formData.dataByFormSectionId.CHANGE_TYPE.dataByFieldId.olo_req_international_services.displayValues[0].value || '';

  const currentFields = [];

  // MOBILE lacks an impactLevel and FIXED lacks a riskValue. Both values are used for similar things though
  const riskValue = selectedNetwork === MOBILE ? getRiskValue(formData) : null;
  const impactLevel = selectedNetwork === FIXED ? getImpactLevel(formData) : null;

  /* <Getting display options for dynamically source display options> */
  const prodCatTwoKey = getGeneratedKey({
    thisField: productCategorisationTwo,
    selectedNetwork,
    dependantFields: [
      productCategorisationOne,
    ],
    values,
  });
  const prodCatTwoDisplayOptions = getDisplayOptionsToUse({
    generatedKey: prodCatTwoKey,
  });

  const prodCatThreeKey = getGeneratedKey({
    thisField: productCategorisationThree,
    selectedNetwork,
    dependantFields: [
      productCategorisationOne,
      productCategorisationTwo,
    ],
    values,
  });
  const prodCatThreeDisplayOptions = getDisplayOptionsToUse({
    generatedKey: prodCatThreeKey,
  });

  const opCatTwoKey = getGeneratedKey({
    thisField: operationalCategorisationTwo,
    selectedNetwork,
    dependantFields: [
      operationalCategorisationOne,
    ],
    values,
  });
  const opCatTwoDisplayOptions = getDisplayOptionsToUse({
    generatedKey: opCatTwoKey,
  });

  const opCatThreeKey = getGeneratedKey({
    thisField: operationalCategorisationThree,
    selectedNetwork,
    dependantFields: [
      operationalCategorisationOne,
      operationalCategorisationTwo,
    ],
    values,
  });
  const opCatThreeDisplayOptions = getDisplayOptionsToUse({
    generatedKey: opCatThreeKey,
  });

  const productNameKey = getGeneratedKey({
    thisField: productName,
    dependantFields: [
      productCategorisationOne,
      productCategorisationTwo,
      productCategorisationThree,
    ],
    values,
  });

  const productNameDisplayOptions = getDisplayOptionsToUse({
    generatedKey: productNameKey,
    showWarning: false,
  });

  const productModelKey = getGeneratedKey({
    thisField: productModel,
    dependantFields: [
      productName,
    ],
    values,
  });
  const productModelDisplayOptions = getDisplayOptionsToUse({
    generatedKey: productModelKey,
    showWarning: false,
  });

  /* </Getting display options for dynamically source display options> */

  /* <Getting display options from endpoiint on mount> */
  const companyHasValue = values[company.id] !== null;

  const getRegionDisplayOptionsOnMount = companyHasValue ? () => getDisplayOptions({
    fieldId: region.id,
    endpointKey: 'change_location',
    preventRefresh: true, // the list is static so we only want to get it once, not every time the component mounts.
    endpointParams: {
      [company.id]: values[company.id],
    },
  }) : null;

  const regionHasValue = values[region.id] !== null;

  const getSiteGroupDisplayOptionsOnMount = (companyHasValue && regionHasValue) ? () => getDisplayOptions({
    fieldId: siteGroup.id,
    endpointKey: 'change_location',
    preventRefresh: true, // the list is static so we only want to get it once, not every time the component mounts.
    endpointParams: {
      [company.id]: values[company.id],
      [region.id]: values[region.id],
    },
  }) : null;

  const siteGroupHasValue = values[siteGroup.id] !== null;

  const getSiteDisplayOptionsOnMount = (companyHasValue && regionHasValue && siteGroupHasValue) ? () => getDisplayOptions({
    fieldId: site.id,
    endpointKey: 'change_location',
    preventRefresh: true, // the list is static so we only want to get it once, not every time the component mounts.
    endpointParams: {
      [company.id]: values[company.id],
      [region.id]: values[region.id],
      [siteGroup.id]: values[siteGroup.id],
    },
  }) : null;

  /* </Getting display options from endpoiint on mount> */

  const filterAndSortService = ({
    componentData,
    // selectedNetwork,
    // isOloCrq,
  }) => {
    if (!selectedNetwork) {
      return [];
    }
    const allDisplayOptions = getDisplayOptionsToUse({
      componentData,
      selectedNetwork,
      isIcmCrq,
      isOloCrq,
      isOLOInternational,
    });
    let normalDisplayOption = [];
    if (isOloCrq) {
      normalDisplayOption = allDisplayOptions;
    }
    else {
      normalDisplayOption = allDisplayOptions;
    }
    // const expeditedDisplayOption = allDisplayOptions.find((option) => ((option.value === EXPEDITED_FIXED) || (option.value === EXPEDITED_MOBILE)));
    if (!normalDisplayOption) {
      // this will likely fire if selectChangeSla tries to render without it's dynamically selected display options.
      // ie, it tries to get display options for 'timing' not 'timing-mobile'
      console.warn(`Cannot find "normalDisplayOption" for ${componentData.id}`);
    }
    const filteredDisplayOptions = [...normalDisplayOption];
    return filteredDisplayOptions;
  };
  // let servicefields = [];
  // impactfields = getImpactFields({
  //   selectedNetwork,
  //   userRole,
  //   hasCrqId,
  //   isOloCrq,
  // });
  currentFields.push({
    ...defaultFieldLogic,
    // componentData: whichServiceNeedsToChange,
    componentData: {
      ...whichServiceNeedsToChange,
      displayOptions: filterAndSortService({
        componentData: whichServiceNeedsToChange,
        selectedNetwork,
        readOnly: false,
        isOloCrq,
      }),
    },
    // we only prepopulate tiers for mobile
    updateValue: selectedNetwork === MOBILE ? prepopulateTiersOnUpdateValue(selectedNetwork) : prepopulateOpcat(selectedNetwork),
  });

  // console.log('sstChangeLocationsstChangeLocation', sstChangeLocation);
  // if (isOloCrq === true && selectedNetwork !== MOBILE && selectedNetwork !== FIXED)
  // {
  //   currentFields.push({
  //     ...defaultFieldLogic,
  //     // componentData: whichServiceNeedsToChange,
  //     componentData: {
  //       ...whichServiceNeedsToChange,
  //       displayOptions: filterAndSortService({
  //         componentData: whichServiceNeedsToChange,
  //         selectedNetwork,
  //         readOnly: false,
  //         isOloCrq,
  //       }),
  //     },
  //     // we only prepopulate tiers for mobile
  //     updateValue: selectedNetwork === MOBILE ? prepopulateTiersOnUpdateValue(selectedNetwork) : null,
  //   });

  // }
  // Company fields for FIXED. May be extracted out at some point once all the fields are implemented
  if (selectedNetwork === FIXED) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: sstChangeLocation,
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...company,
        vFParams: {
          ...company.vFParams,
          getDisplayOptionsOnUpdate: getSiteDisplayOptionsForNextTier({
            thisFieldId: company.id,
            fieldIdDisplayOptionsToGet: region.id,
            displayOptionsToClear: [region.id, siteGroup.id, site.id],
          }),
        },
      },
      updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
        thisFieldId: company.id,
        affectedFields: [
          region,
          siteGroup,
          site,
        ],
      }),
    });

    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...region,
        vFParams: {
          ...region.vFParams,
          getDisplayOptionsOnMount: getRegionDisplayOptionsOnMount,
          getDisplayOptionsOnUpdate: getSiteDisplayOptionsForNextTier({
            thisFieldId: region.id,
            fieldIdDisplayOptionsToGet: siteGroup.id,
            displayOptionsToClear: [siteGroup.id, site.id],
            endpointParams: {
              [company.id]: values[company.id],
            },
          }),
        },
      },
      updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
        thisFieldId: region.id,
        affectedFields: [
          siteGroup,
          site,
        ],
      }),
      disabledFieldOptions: {
        disabled: !companyHasValue,
        // NOTE! This can silently break the summary page due to typos
        // Once we have a better understanding of the specification we may need to do a
        // #disabledValueRefactor
        disabledValue: null,
        unDisabledValue: null,
      },
    });

    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...siteGroup,
        vFParams: {
          ...siteGroup.vFParams,
          getDisplayOptionsOnMount: getSiteGroupDisplayOptionsOnMount,
          getDisplayOptionsOnUpdate: getSiteDisplayOptionsForNextTier({
            thisFieldId: siteGroup.id,
            fieldIdDisplayOptionsToGet: site.id,
            displayOptionsToClear: [site.id],
            endpointParams: {
              [company.id]: values[company.id],
              [region.id]: values[region.id],
            },
          }),
        },
      },
      updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
        thisFieldId: siteGroup.id,
        affectedFields: [
          site,
        ],
      }),
      disabledFieldOptions: {
        disabled: !companyHasValue || !regionHasValue,
        // NOTE! This can silently break the summary page due to typos
        // Once we have a better understanding of the specification we may need to do a
        // #disabledValueRefactor
        disabledValue: null,
        unDisabledValue: null,
      },
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...site,
        vFParams: {
          ...site.vFParams,
          getDisplayOptionsOnMount: getSiteDisplayOptionsOnMount,
        },
      },
      disabledFieldOptions: {
        disabled: !companyHasValue || !regionHasValue || !siteGroupHasValue,
        // NOTE! This can silently break the summary page due to typos
        // Once we have a better understanding of the specification we may need to do a
        // #disabledValueRefactor
        disabledValue: null,
        unDisabledValue: null,
      },
    });
  }

  currentFields.push({
    ...defaultFieldLogic,
    componentData: summaryOfChange,
  });
  if ((selectedNetwork === FIXED && (impactLevel === IMPACT_LEVEL_1 || impactLevel === IMPACT_LEVEL_2)) || (selectedNetwork === MOBILE && (riskValue === RISK_LEVEL_3 || riskValue === RISK_LEVEL_4))) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: changeDetailsNotes,
    });
  }
  else {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...changeDetailsNotes,
        vFParams: {
          ...changeDetailsNotes.vFParams,
          fieldSubTitle: '',
        },
      },
    });
  }
  if (selectedNetwork === FIXED && (impactLevel === IMPACT_LEVEL_1 || impactLevel === IMPACT_LEVEL_2)) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: customerImpactStatement,
    });
  }

  if (selectedNetwork === MOBILE && (riskValue === RISK_LEVEL_3 || riskValue === RISK_LEVEL_4)) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: customerImpactStatement,
    });
  }

  const mobileChangeReasonInitialValue = (roleForNetwork === COORDINATOR) ? NON_PROJECT_MOBILE : PROJECT_MOBILE;
  // if (isOloCrq !== true){
  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...changeReason,
      initialValues: mobileChangeReasonInitialValue,
    },
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '3000',
    },
  });
  // }
  // The first productCategorisation is mandatory. After that, they are optional, but disabled until the tier before is selected
  // if (isOloCrq !== true){
  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...productCategorisationOne,
      initialValues: null,
    },
    updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
      thisFieldId: productCategorisationOne.id,
      affectedFields: [
        productCategorisationTwo,
        productCategorisationThree,
        productName,
        productModel,
        manufacturer,
      ],
    }),
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: 'Network',
    },
  });
  // }

  // if (isOloCrq !== true){
  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...productCategorisationTwo,
      displayOptions: prodCatTwoDisplayOptions,
      initialValues: null,
    },
    optional: true,
    updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
      thisFieldId: productCategorisationTwo.id,
      affectedFields: [
        productCategorisationThree,
        productName,
        productModel,
        manufacturer,
      ],
    }),
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: null,
    },
  });
  // }
  // if (isOloCrq !== true){
  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...productCategorisationThree,
      displayOptions: prodCatThreeDisplayOptions,
      initialValues: null,
    },
    optional: true,
    updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
      thisFieldId: productCategorisationThree.id,
      affectedFields: [
        productName,
        productModel,
        manufacturer,
      ],
    }),
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: null,
    },
  });
  // }


  /* FIXED only fields */
  if (selectedNetwork === FIXED) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...productName,
        displayOptions: productNameDisplayOptions,
      },
      updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
        thisFieldId: productName.id,
        affectedFields: [
          productModel,
          manufacturer,
        ],
      }),
      shouldRender: !isOloCrq,
      shouldValidate: !isOloCrq,
      optional: true,
    });

    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...productModel,
        displayOptions: productModelDisplayOptions,
      },
      updateValue: setManufacturerValueBasedOnModel(selectedNetwork),
      optional: true,
      shouldRender: !isOloCrq,
      shouldValidate: !isOloCrq,
    });

    currentFields.push({
      ...defaultFieldLogic,
      componentData: manufacturer,
      readOnly: true,
      shouldRender: !isOloCrq,
      shouldValidate: !isOloCrq,
    });
  }

  // always show operationalCategorisation. After that, the next tiers are disabled until the tier before is selected
  // if (isOloCrq !== true) {
  currentFields.push({
    ...defaultFieldLogic,
    componentData: operationalCategorisationOne,
    updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
      thisFieldId: operationalCategorisationOne.id,
      affectedFields: [
        operationalCategorisationTwo,
        operationalCategorisationThree,
      ],
    }),
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: values.services,
    },
  });

  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...operationalCategorisationTwo,
      displayOptions: opCatTwoDisplayOptions,
    },
    optional: true,
    updateValue: updateThisValueThenSetAffectedFieldsToInitialValue({
      thisFieldId: operationalCategorisationTwo.id,
      affectedFields: [
        operationalCategorisationThree,
      ],
    }),
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: null,
    },
  });
  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...operationalCategorisationThree,
      displayOptions: opCatThreeDisplayOptions,
    },
    optional: true,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: null,
    },
  });

  // }


  /* MOBILE only fields */
  if (selectedNetwork === MOBILE) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: tmbs,
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: babt,
      optional: true,
    });
    const babtValue = values[babt.id];
    const iarNumberOptional = (babtValue !== RECORDABLE) && (babtValue !== NOTIFIABLE);
    currentFields.push({
      ...defaultFieldLogic,
      componentData: iarNumber,
      optional: iarNumberOptional,
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: mvno,
    });
    if (riskValue === RISK_LEVEL_3 || riskValue === RISK_LEVEL_4) {
      currentFields.push({
        ...defaultFieldLogic,
        componentData: {
          ...commQuestion,
          initialValues: ['Yes'],
        },
      });
      currentFields.push({
        ...defaultFieldLogic,
        componentData: paragraphCommRequired,
      });
    }
    else if (riskValue !== RISK_LEVEL_3 || riskValue !== RISK_LEVEL_4) {
      currentFields.push({
        ...defaultFieldLogic,
        componentData: {
          ...commQuestion,
          initialValues: ['No'],
        },
        readOnly: true,
      });
      currentFields.push({
        ...defaultFieldLogic,
        componentData: paragraphCommRequired,
      });
    }
    if (roleForNetwork === COORDINATOR) {
      currentFields.push({
        ...defaultFieldLogic,
        componentData: assignSponsor,
        optional: (roleForNetwork === COORDINATOR) ? false : true,
      });
    }
  }
  return currentFields;
};

// generates the formSection!
const SectionFour = React.memo(({
  // saves locally and goes to the next page
  handleSuccessfulSubmit,
  userRoles,
  formData,
  navigationProps,
  sectionIndex,
  editingStatus,
}) => {
  // Props for the SectionNav in FormSection. these come from the parent but we mutate them to be relivent to the section
  const filteredNavigationProps = {
    ...navigationProps,
    submitForm: null,
  };


  const formattedSectionTitle = prefixStringWithIndexPlusOne(sectionTitle, sectionIndex);


  return (
    <FormSection
      sectionTitle={formattedSectionTitle}
      sectionId={sectionId}
      getCurrentFields={getCurrentFields}
      userRoles={userRoles}
      formData={formData}
      navigationProps={filteredNavigationProps}
      // This passes up the current section data too
      handleSuccessfulSubmit={handleSuccessfulSubmit}
      editingStatus={editingStatus}
    />
  );
});


SectionFour.propTypes = {
  handleSuccessfulSubmit: PropTypes.func,
  userRoles: userRolesPropShape,
  formData: formDataShape,
  navigationProps: navigationPropsShape,
  sectionIndex: PropTypes.number,
  editingStatus: PropTypes.string,
};

export {
  SectionFour,
  getCurrentFields,
  CHANGE_DETAILS,
  getGeneratedKey,
};
